/**
 * useBillingReturn — handle the return from a Stripe-hosted page (Phase 13).
 *
 * The backend sends Checkout back to `/dashboard?billing=success` (or `=canceled`), card capture
 * (Checkout in setup mode) back to `/dashboard?billing=card` and the customer portal back to
 * `/dashboard?billing=portal`. On load this hook opens Organization
 * Account, shows a toast, re-reads permissions once after ~3 s (the Stripe webhook can lag; the
 * page load itself already read them, so there is no immediate re-read), and strips the parameter
 * from the URL. A canceled Checkout changed nothing, so it re-reads nothing.
 *
 * @module hooks/useBillingReturn
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authcontext';
import { useToast } from '../components/common/Toast';

export type BillingReturn = 'success' | 'canceled' | 'portal' | 'card';

const BILLING_RETURNS: readonly BillingReturn[] = ['success', 'canceled', 'portal', 'card'];

/** Delay of the permissions re-read after a return from Stripe (webhook lag). */
export const BILLING_RETURN_REFETCH_MS = 3000;

/** The `billing` query value, when it is one we know. */
export function parseBillingReturn(search: string): BillingReturn | null {
  const value = new URLSearchParams(search).get('billing');
  return BILLING_RETURNS.find((known) => known === value) ?? null;
}

export function useBillingReturn(onOpenAccount?: () => void): void {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshPermissions } = useAuth();
  const { showToast } = useToast();
  const handled = useRef(false);
  const timer = useRef<number | null>(null);
  const openAccount = useRef(onOpenAccount);
  openAccount.current = onOpenAccount;

  useEffect(() => {
    const result = parseBillingReturn(location.search);
    if (!result || handled.current) return;
    handled.current = true;
    openAccount.current?.();
    if (result === 'success') {
      showToast('Payment details saved — your account is being updated', { variant: 'success' });
    } else if (result === 'card') {
      showToast('Card saved — your account is being updated', { variant: 'success' });
    } else if (result === 'canceled') {
      showToast('Checkout canceled', { variant: 'info' });
    }
    if (result !== 'canceled') {
      timer.current = window.setTimeout(() => {
        timer.current = null;
        void refreshPermissions?.();
      }, BILLING_RETURN_REFETCH_MS);
    }
    const params = new URLSearchParams(location.search);
    params.delete('billing');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [location.search, location.pathname, navigate, refreshPermissions, showToast]);

  // Clear the pending re-read only on unmount (the URL strip re-runs the effect above)
  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);
}

export default useBillingReturn;
